import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Sheet } from '@pkg/components';
import { GroupGridLoading, GroupGridNotification } from '@pkg/components/Sections/Groups';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useAppDispatch, useGetGroup, useGroupFilterCount, useUpdateGroup } from '@pkg/hooks';

import { type GroupItem, type SocialGroupTypeOverride } from '@app/client/common/structs/groups/groups';
import { NoMatchingResults } from '@app/pages/Prototype/NftManagerPanel/components/SidePanelContent/components/ManagerGrid/components';
import { addNotification } from '@app/store/actions/notification';

import { NftItem } from './components';
import { nftItemIsActive, nftItemMatchesSearch } from './utilities';

import { NftListWrapper, Pagination } from './styles';

interface UpdateHeaderImageCriteria {
    id: SocialGroupTypeOverride['id'];
    // TODO: For some reason, this is not inheriting the `null` type.
    name?: SocialGroupTypeOverride['name'] | null;
}

export interface NftListProps {
    items: GroupItem[];
    searchValue?: string;
    loading?: boolean;
    hasError?: boolean;
    hasMoreResults?: boolean;
    fetchingResults?: boolean;
    staleResults?: boolean;
    onLoadMore?(): void;
}

// TODO: This component is very similar to (and should be consolidated):
// src/components/Groups/GroupGrid/GroupGrid.tsx
export function NftList({
    items,
    searchValue = '',
    loading = false,
    hasError = false,
    hasMoreResults = false,
    fetchingResults = false,
    staleResults = false,
    onLoadMore,
}: NftListProps) {
    const { t: translate } = useTranslation(['groups']);

    const dispatch = useAppDispatch();
    const { ref, inView } = useInView();

    const numberOfFilters = useGroupFilterCount();

    const { data: groupData } = useGetGroup();
    const { mutate } = useUpdateGroup();

    function handleUpdateHeaderImage({ id, name }: UpdateHeaderImageCriteria) {
        if (!groupData?.id) {
            return;
        }

        // Set to `null` in order to "remove" the selected item.
        const newValue = groupData.header_yat_page_item_id === id ? null : id;

        mutate(
            {
                id: groupData.id,
                value: {
                    header_yat_page_item_id: newValue,
                },
            },
            {
                onSuccess() {
                    dispatch(
                        addNotification({
                            message: newValue
                                ? translate('pods_image_success', { name })
                                : translate('pods_image_removed', { name }),
                        }),
                    );
                },
                onError({ message }) {
                    dispatch(addNotification({ message }));
                },
            },
        );
    }

    useEffect(() => {
        if (inView && !fetchingResults && onLoadMore) {
            onLoadMore();
        }
    }, [inView, fetchingResults, onLoadMore]);

    if (loading) return <GroupGridLoading />;

    if (hasError) {
        return <GroupGridNotification heading={translate('groups_error_results')} />;
    }

    const noItems = items.length === 0;

    if (numberOfFilters === 0 && noItems) {
        return (
            <GroupGridNotification
                heading={translate('groups_no_results_title')}
                subheading={translate('groups_no_results_body')}
            />
        );
    }

    if (noItems) {
        return (
            <GroupGridNotification
                heading={translate('groups_no_results_with_filters_title')}
                subheading={translate('groups_no_results_with_filters_body')}
            />
        );
    }

    const filteredItems =
        searchValue.length && items
            ? items.filter(({ item_type }) => nftItemMatchesSearch(item_type.Nft, searchValue))
            : items;

    const itemsMarkup = filteredItems?.map(({ id, item_type }) => {
        return (
            <NftItem
                key={id}
                item={item_type.Nft}
                active={nftItemIsActive({
                    itemId: id,
                    podImageId: groupData?.header_yat_page_item_id,
                })}
                onAction={() => handleUpdateHeaderImage({ id, name: item_type.Nft.name })}
            />
        );
    });

    const resultsMarkup = itemsMarkup?.length ? <Sheet.Layout.Results>{itemsMarkup}</Sheet.Layout.Results> : null;

    return (
        <NftListWrapper>
            {resultsMarkup || <NoMatchingResults />}

            {hasMoreResults && onLoadMore ? (
                <Pagination>
                    <Button
                        ref={ref}
                        disabled={fetchingResults || staleResults}
                        loading={fetchingResults}
                        onClick={onLoadMore}
                    >
                        {translate('groups_btn_load_more')}
                    </Button>
                </Pagination>
            ) : null}
        </NftListWrapper>
    );
}
